import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { trackDownload, trackButtonClick } from '../../../utils/googleAnalyticsService';

const DownloadOptions = ({ onDownload, onEmailSend, isGenerating, tripId, tripTitle }) => {
  const [selectedFormat, setSelectedFormat] = useState('detailed');
  const [emailAddress, setEmailAddress] = useState('');
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [isEmailSending, setIsEmailSending] = useState(false);

  const formatOptions = [
    {
      id: 'detailed',
      name: 'Detailed Itinerary',
      description: 'Complete multi-page PDF with all activities and information',
      icon: 'FileText',
      pages: '4-6 pages'
    },
    {
      id: 'summary',
      name: 'Quick Summary',
      description: 'Condensed single-page overview for quick reference',
      icon: 'File',
      pages: '1 page'
    }
  ];

  const handleDownload = (format) => {
    // Track download action
    trackDownload(`${tripTitle}_trip_itinerary.${format}`, format);
    trackButtonClick(`download_${format}`, `Download ${format?.toUpperCase()}`, {
      trip_id: tripId,
      format,
    });

    onDownload(format);
  };

  const handleEmailSend = async () => {
    if (!emailAddress) return;
    
    setIsEmailSending(true);
    try {
      await onEmailSend(emailAddress, selectedFormat);
      setEmailAddress('');
      setShowEmailForm(false);
    } catch (error) {
      console.error('Email send failed:', error);
    } finally {
      setIsEmailSending(false);
    }
  };

  return (
    <div className="bg-card border border-border rounded-organic shadow-organic">
      <div className="p-6 border-b border-border">
        <h3 className="text-lg font-heading font-heading-bold text-card-foreground">Download Options</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Choose your preferred format and download method
        </p>
      </div>
      <div className="p-6 space-y-6">
        {/* Format Selection */}
        <div>
          <h4 className="text-sm font-body font-body-semibold text-foreground mb-3">PDF Format</h4>
          <div className="space-y-3">
            {formatOptions?.map((format) => (
              <label
                key={format?.id}
                className={`flex items-start space-x-3 p-4 border rounded-organic-sm cursor-pointer transition-organic ${
                  selectedFormat === format?.id
                    ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                }`}
              >
                <input
                  type="radio"
                  name="format"
                  value={format?.id}
                  checked={selectedFormat === format?.id}
                  onChange={(e) => setSelectedFormat(e?.target?.value)}
                  className="sr-only"
                />
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                  selectedFormat === format?.id
                    ? 'border-primary bg-primary' :'border-border'
                }`}>
                  {selectedFormat === format?.id && (
                    <div className="w-2 h-2 bg-white rounded-full" />
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <Icon name={format?.icon} size={16} className="text-foreground" />
                    <span className="font-body font-body-medium text-foreground">{format?.name}</span>
                    <span className="text-xs text-muted-foreground">({format?.pages})</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">{format?.description}</p>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Download Actions */}
        <div className="space-y-4">
          <h4 className="text-sm font-body font-body-semibold text-foreground">Download Method</h4>
          
          {/* Direct Download */}
          <div className="flex items-center justify-between p-4 border border-border rounded-organic-sm">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-primary/10 rounded-organic flex items-center justify-center">
                <Icon name="Download" size={20} className="text-primary" />
              </div>
              <div>
                <p className="font-body font-body-medium text-foreground">Direct Download</p>
                <p className="text-sm text-muted-foreground">Save PDF to your device</p>
              </div>
            </div>
            <Button
              variant="default"
              onClick={() => handleDownload(selectedFormat)}
              disabled={isGenerating}
              loading={isGenerating}
              iconName="Download"
              iconPosition="left"
            >
              Download PDF
            </Button>
          </div>

          {/* Email Option */}
          <div className="border border-border rounded-organic-sm">
            <div className="flex items-center justify-between p-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-accent/10 rounded-organic flex items-center justify-center">
                  <Icon name="Mail" size={20} className="text-accent" />
                </div>
                <div>
                  <p className="font-body font-body-medium text-foreground">Email PDF</p>
                  <p className="text-sm text-muted-foreground">Send to your email address</p>
                </div>
              </div>
              <Button
                variant="outline"
                onClick={() => setShowEmailForm(!showEmailForm)}
                iconName={showEmailForm ? "ChevronUp" : "ChevronDown"}
                iconPosition="right"
              >
                Email Options
              </Button>
            </div>

            {showEmailForm && (
              <div className="border-t border-border p-4 space-y-4">
                <Input
                  type="email"
                  label="Email Address"
                  placeholder="Enter your email address"
                  value={emailAddress}
                  onChange={(e) => setEmailAddress(e?.target?.value)}
                  required
                />
                <div className="flex items-center space-x-3">
                  <Button
                    variant="default"
                    onClick={handleEmailSend}
                    disabled={!emailAddress || isEmailSending}
                    loading={isEmailSending}
                    iconName="Send"
                    iconPosition="left"
                  >
                    Send Email
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => setShowEmailForm(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* File Information */}
        <div className="bg-muted/50 rounded-organic-sm p-4">
          <div className="flex items-start space-x-3">
            <Icon name="Info" size={16} className="text-primary mt-0.5" />
            <div className="text-sm">
              <p className="font-body font-body-medium text-foreground mb-1">File Information</p>
              <ul className="text-muted-foreground space-y-1">
                <li>• PDF format compatible with all devices</li>
                <li>• Optimized for both digital viewing and printing</li>
                <li>• Includes clickable links to park websites</li>
                <li>• File size: approximately 2-5 MB</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DownloadOptions;